import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Vision / incident & police-presence audit `data` schemas. See `registry.ts` for the
// audit-data leniency contract these follow. These payloads originate in the Vision
// (police-facing) app as deeply-nested objects, so the schemas are deeply optional.
// Shared sub-shapes.
// All fields optional+nullable to cover both place-of-safety source shapes.
const placeOfSafetySchema = z.object({
    id: z.string().nullish(),
    name: z.string().nullish(),
    address: z.string().nullish(),
    postalCode: z.string().nullish(),
    odsCode: z.string().nullish(),
    eMHACuratedPlaceOfSafetyId: z.string().nullish(),
    trustId: z.string().nullish(),
});
const visionUserSchema = z.object({
    id: z.string().nullish(),
    email: z.string().nullish(),
    name: z.string().nullish(),
});
const visionOrganisationSchema = z.object({
    id: z.string().nullish(),
    name: z.string().nullish(),
});
// The `{ value, valueLabel }` pair used throughout the V2 police-presence payloads.
const labelledValueSchema = z.object({
    value: z.string().nullish(),
    valueLabel: z.string().nullish(),
});
const valueSchema = z.object({ value: z.string().nullish() });
// ===== Incidents =====
export const visionShareIncidentDataSchema = z.object({
    email: z.string().nullish(),
    incidentType: z.string().nullish(),
});
export const visionAcceptIncidentDataSchema = z.object({
    incidentType: z.string().nullish(),
    officerDetails: z.object({ name: z.string().nullish() }).nullish(),
    placeOfSafety: placeOfSafetySchema.nullish(),
    isCurrentPlaceOfSafety: z.boolean().nullish(),
});
export const visionRejectIncidentDataSchema = z.object({
    incidentType: z.string().nullish(),
    officerDetails: z.object({ name: z.string().nullish() }).nullish(),
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(), // PII
});
// No payload modelled; kept for any historical rows.
export const visionPreviewIncidentDataSchema = z.object({});
// Shared by visionShareCodeSuccessExchange / visionShareCodeFailureExchange.
export const visionShareCodeExchangeDataSchema = z.object({
    stage: z.string().nullish(),
});
// ===== Police presence =====
export const visionPoliceEndIncidentDataSchema = z.object({
    user: visionUserSchema.nullish(),
    organisation: visionOrganisationSchema.nullish(),
});
// visionPoliceAcceptRequestToStay / visionPoliceDeclineRequestToStay share a shape.
export const visionPoliceRequestToStayDataSchema = z.object({
    user: visionUserSchema.nullish(),
    organisation: visionOrganisationSchema.nullish(),
    placeOfSafety: placeOfSafetySchema.nullish(),
    reason: z.string().nullish(),
});
// Legacy V1 police-presence request, superseded by …V2; kept for historical rows.
export const visionRequestPolicePresenceDataSchema = z.object({
    policePresenceRequested: z
        .object({
        type: z.string().nullish(),
        reasonLabel: z.string().nullish(),
        furtherReasoning: z.string().nullish(), // PII
        hasPolicePresenceBeenRequestedLabelValue: z.string().nullish(),
    })
        .nullish(),
    placeOfSafety: placeOfSafetySchema.nullish(),
});
export const visionRequestPolicePresenceV2DataSchema = z.object({
    policePresenceRequested: z
        .object({
        hasPolicePresenceRequested: labelledValueSchema.nullish(),
        policePresenceRequestedReason: labelledValueSchema.nullish(),
        furtherReasoning: valueSchema.nullish(), // PII
    })
        .nullish(),
    recordPolicePresence: z
        .object({
        hasRemainedAtPlaceOfSafety: labelledValueSchema.nullish(),
        officerName: valueSchema.nullish(),
        reasoning: valueSchema.nullish(), // PII
    })
        .nullish(),
    placeOfSafety: placeOfSafetySchema.nullish(),
});
export const visionResponsePolicePresenceDataSchema = z.object({
    policePresenceRequestData: z
        .object({
        hasPolicePresenceRequested: z.string().nullish(),
        hasPolicePresenceRequestedLabel: z.string().nullish(),
        policePresenceRequestedReason: z.string().nullish(),
        policePresenceRequestedReasonLabel: z.string().nullish(),
    })
        .nullish(),
    placeOfSafety: placeOfSafetySchema.nullish(),
    healthProfessionalName: z.string().nullish(),
    recordPolicePresence: z
        .object({
        hasRemainedAtPlaceOfSafetyValue: z.string().nullish(),
        hasRemainedAtPlaceOfSafetyLabel: z.string().nullish(),
        officerName: z.string().nullish(),
        reasoning: z.string().nullish(), // PII
    })
        .nullish(),
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const visionAuditDataRegistry = {
    [AuditTrailEntryType.visionShareIncident]: visionShareIncidentDataSchema,
    [AuditTrailEntryType.visionAcceptIncident]: visionAcceptIncidentDataSchema,
    [AuditTrailEntryType.visionRejectIncident]: visionRejectIncidentDataSchema,
    [AuditTrailEntryType.visionPreviewIncident]: visionPreviewIncidentDataSchema,
    [AuditTrailEntryType.visionShareCodeSuccessExchange]: visionShareCodeExchangeDataSchema,
    [AuditTrailEntryType.visionShareCodeFailureExchange]: visionShareCodeExchangeDataSchema,
    [AuditTrailEntryType.visionPoliceEndIncident]: visionPoliceEndIncidentDataSchema,
    [AuditTrailEntryType.visionRequestPolicePresence]: visionRequestPolicePresenceDataSchema,
    [AuditTrailEntryType.visionRequestPolicePresenceV2]: visionRequestPolicePresenceV2DataSchema,
    [AuditTrailEntryType.visionResponsePolicePresence]: visionResponsePolicePresenceDataSchema,
    [AuditTrailEntryType.visionPoliceAcceptRequestToStay]: visionPoliceRequestToStayDataSchema,
    [AuditTrailEntryType.visionPoliceDeclineRequestToStay]: visionPoliceRequestToStayDataSchema,
};
//# sourceMappingURL=vision.js.map